'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface CustomCalendarProps {
  className?: string;
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  bookedDates?: Date[];
  mode?: 'single' | 'multiple';
}

export function CustomCalendar({
  className,
  selected,
  onSelect,
  disabled,
  bookedDates = [],
  mode = 'single',
  ...props
}: CustomCalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(
    selected || new Date()
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Get all days for the calendar grid (including padding days from prev/next month)
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(calendarStart.getDate() - monthStart.getDay());

  const calendarEnd = new Date(monthEnd);
  calendarEnd.setDate(calendarEnd.getDate() + (6 - monthEnd.getDay()));

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleDateClick = (date: Date) => {
    if (disabled && disabled(date)) {
      return;
    }
    onSelect?.(date);
  };

  const isDateBooked = (date: Date) => {
    return bookedDates.some(bookedDate => isSameDay(date, bookedDate));
  };

  const isDateSelected = (date: Date) => {
    return selected && isSameDay(date, selected);
  };

  const isDateDisabled = (date: Date) => {
    return disabled ? disabled(date) : false;
  };

  return (
    <div className={cn('custom-calendar p-3', className)} {...props}>
      {/* Header with navigation */}
      <div className="calendar-header flex items-center justify-between mb-4 relative">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={goToPreviousMonth}
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <h2 className="text-sm font-medium">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={goToNextMonth}
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {/* Weekday headers */}
        <div className="weekday-headers grid grid-cols-7 mb-2">
          {WEEKDAYS.map(day => (
            <div
              key={day}
              className="weekday-header text-center text-xs font-normal text-muted-foreground py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Date cells */}
        <div className="date-grid grid grid-cols-7 gap-1">
          {calendarDays.map((date, index) => {
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isSelected = isDateSelected(date);
            const isDisabled = isDateDisabled(date);
            const isDateToday = isToday(date);
            const isBooked = isDateBooked(date);

            return (
              <div key={date.toISOString()} className="date-cell relative">
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    'h-8 w-8 p-0 font-normal relative hover:bg-accent hover:text-accent-foreground',
                    !isCurrentMonth && 'text-muted-foreground opacity-50',
                    isDateToday &&
                      !isSelected &&
                      'bg-accent text-accent-foreground',
                    isDisabled &&
                      'text-muted-foreground opacity-50 cursor-not-allowed',
                    isSelected &&
                      'bg-tifpa-rose text-white hover:bg-tifpa-rose hover:text-white hover:brightness-95'
                  )}
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabled}
                >
                  {format(date, 'd')}
                  {isBooked && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-tifpa-rose rounded-full" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
